import { useState, useRef } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import BatchResults, { type CreationResult } from '@/components/admin/BatchResults'
import {
  ArrowLeft,
  UserPlus,
  Users,
  Upload,
  Loader2,
  CheckCircle,
  XCircle,
  Trash2,
  Download,
  FileText,
  AlertCircle
} from 'lucide-react'

interface StudentForm {
  email: string
  password: string
  first_name: string
  last_name: string
  phone: string
  birth_date: string
  age_group: 'children' | 'adult'
  subscription_status: 'trial' | 'active'
  program_start_date: string
  payment_amount: string
}

const emptyForm: StudentForm = {
  email: '',
  password: '',
  first_name: '',
  last_name: '',
  phone: '',
  birth_date: '',
  age_group: 'adult',
  subscription_status: 'trial',
  program_start_date: '',
  payment_amount: ''
}

type Tab = 'single' | 'batch' | 'import'

export default function StudentCreate() {
  const [activeTab, setActiveTab] = useState<Tab>('single')
  const [form, setForm] = useState<StudentForm>({ ...emptyForm })
  const [isCreating, setIsCreating] = useState(false)
  const [singleResult, setSingleResult] = useState<CreationResult | null>(null)

  // Batch state
  const [batchList, setBatchList] = useState<StudentForm[]>([])
  const [batchResults, setBatchResults] = useState<CreationResult[]>([])
  const [batchProgress, setBatchProgress] = useState(0)
  const [isBatchRunning, setIsBatchRunning] = useState(false)

  // Import state
  const [csvData, setCsvData] = useState<StudentForm[]>([])
  const [csvError, setCsvError] = useState('')
  const [csvTotalAmount, setCsvTotalAmount] = useState('')
  const fileInputRef = useRef<HTMLInputElement>(null)

  const updateForm = (field: keyof StudentForm, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }))
  }

  const validateForm = (f: StudentForm): string | null => {
    if (!f.email || !f.email.includes('@')) return 'Email inválido'
    if (!f.password || f.password.length < 6) return 'Contraseña mínimo 6 caracteres'
    if (!f.first_name.trim()) return 'Nombre requerido'
    if (!f.last_name.trim()) return 'Apellido requerido'
    if (!f.birth_date) return 'Fecha de nacimiento requerida'
    return null
  }

  const createStudent = async (data: StudentForm): Promise<CreationResult> => {
    try {
      const { data: result, error } = await (supabase.rpc as any)('admin_create_student', {
        p_email: data.email.trim(),
        p_password: data.password,
        p_first_name: data.first_name.trim(),
        p_last_name: data.last_name.trim(),
        p_phone: data.phone.trim() || null,
        p_birth_date: data.birth_date,
        p_age_group: data.age_group,
        p_subscription_status: data.subscription_status
      })

      if (error) {
        return { email: data.email, name: `${data.first_name} ${data.last_name}`, success: false, error: error.message }
      }

      if (result && !result.success) {
        return { email: data.email, name: `${data.first_name} ${data.last_name}`, success: false, error: result.error }
      }

      // Guardar program_start_date y payment_amount si se proporcionaron
      const extraFields: Record<string, any> = {}
      if (data.program_start_date) extraFields.program_start_date = data.program_start_date
      if (data.payment_amount &&  Number.parseFloat(data.payment_amount) > 0) extraFields.payment_amount =  Number.parseFloat(data.payment_amount)

      if (Object.keys(extraFields).length > 0) {
        const { data: profile } = await (supabase
          .from('profiles') as any)
          .select('id')
          .eq('email', data.email.trim())
          .single()
        if (profile) {
          await (supabase.from('students') as any)
            .update(extraFields)
            .eq('profile_id', profile.id)
        }
      }

      return { email: data.email, name: result?.name || `${data.first_name} ${data.last_name}`, success: true }
    } catch (err: any) {
      return { email: data.email, name: `${data.first_name} ${data.last_name}`, success: false, error: err.message }
    }
  }

  // === SINGLE ===
  const handleCreateSingle = async () => {
    const err = validateForm(form)
    if (err) {
      setSingleResult({ email: form.email, name: '', success: false, error: err })
      return
    }

    setIsCreating(true)
    setSingleResult(null)
    const result = await createStudent(form)
    setSingleResult(result)
    if (result.success) {
      setForm({ ...emptyForm })
    }
    setIsCreating(false)
  }

  // === BATCH ===
  const handleAddToBatch = () => {
    const err = validateForm(form)
    if (err) {
      setSingleResult({ email: form.email, name: '', success: false, error: err })
      return
    }

    // Check for duplicate email
    if (batchList.some(s => s.email.toLowerCase() === form.email.toLowerCase())) {
      setSingleResult({ email: form.email, name: '', success: false, error: 'Este email ya está en la lista' })
      return
    }

    setBatchList(prev => [...prev, { ...form }])
    setForm({ ...emptyForm })
    setSingleResult(null)
  }

  const handleRemoveFromBatch = (index: number) => {
    setBatchList(prev => prev.filter((_, i) => i !== index))
  }

  const handleCreateBatch = async () => {
    if (batchList.length === 0) return

    setIsBatchRunning(true)
    setBatchResults([])
    setBatchProgress(0)

    const results: CreationResult[] = []
    for (let i = 0; i < batchList.length; i++) {
      const result = await createStudent(batchList[i])
      results.push(result)
      setBatchResults([...results])
      setBatchProgress(Math.round(((i + 1) / batchList.length) * 100))
    }

    // Remove successfully created students from the list
    const failedEmails = results.filter(r => !r.success).map(r => r.email.toLowerCase())
    setBatchList(prev => prev.filter(s => failedEmails.includes(s.email.toLowerCase())))
    setIsBatchRunning(false)
  }

  // === IMPORT ===
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    setCsvError('')
    setCsvData([])

    const reader = new FileReader()
    reader.onload = (event) => {
      try {
        const text = event.target?.result as string
        const lines = text.split('\n').map(l => l.trim()).filter(l => l.length > 0)

        if (lines.length < 2) {
          setCsvError('El archivo debe tener al menos una fila de encabezado y una de datos')
          return
        }

        // Skip header
        const dataLines = lines.slice(1)
        const parsed: StudentForm[] = []

        for (let i = 0; i < dataLines.length; i++) {
          const cols = dataLines[i].split(',').map(c => c.trim().replaceAll(/^["']|["']$/g, ''))

          if (cols.length < 6) {
            setCsvError(`Fila ${i + 2}: faltan columnas (mínimo 6: email, password, nombre, apellido, teléfono, nacimiento)`)
            return
          }

          parsed.push({
            email: cols[0],
            password: cols[1],
            first_name: cols[2],
            last_name: cols[3],
            phone: cols[4] || '',
            birth_date: cols[5],
            age_group: (cols[6] === 'children' ? 'children' : 'adult') as 'children' | 'adult',
            subscription_status: (cols[7] === 'active' ? 'active' : 'trial') as 'trial' | 'active',
            program_start_date: cols[8] || '',
            payment_amount: ''
          })
        }

        setCsvData(parsed)
      } catch {
        setCsvError('Error al leer el archivo. Verifica que sea un CSV válido.')
      }
    }
    reader.readAsText(file)
  }

  const handleImportCsv = async () => {
    if (csvData.length === 0) return

    setIsBatchRunning(true)
    setBatchResults([])
    setBatchProgress(0)

    // Calcular monto por alumno si se ingresó monto total
    const perStudentAmount = csvTotalAmount && Number.parseFloat(csvTotalAmount)> 0
      ? String(Math.round(Number.parseFloat(csvTotalAmount) / csvData.length))
      : ''

    const results: CreationResult[] = []
    for (let i = 0; i < csvData.length; i++) {
      const studentData = { ...csvData[i], payment_amount: perStudentAmount }
      const result = await createStudent(studentData)
      results.push(result)
      setBatchResults([...results])
      setBatchProgress(Math.round(((i + 1) / csvData.length) * 100))
    }

    // Remove successfully imported from preview
    const failedEmails = results.filter(r => !r.success).map(r => r.email.toLowerCase())
    setCsvData(prev => prev.filter(s => failedEmails.includes(s.email.toLowerCase())))
    setIsBatchRunning(false)
  }

  const downloadTemplate = () => {
    const csv = 'email,password,nombre,apellido,telefono,fecha_nacimiento,grupo_edad,suscripcion\njuan@ejemplo.com,Password123,Juan,Pérez,+56912345678,2000-01-15,adult,trial\nmaria@ejemplo.com,Password456,María,García,+56987654321,2010-05-20,children,active'
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'plantilla_alumnos.csv'
    a.click()
    URL.revokeObjectURL(url)
  }

  const tabs = [
    { id: 'single' as Tab, label: 'Crear alumno', icon: UserPlus },
    { id: 'batch' as Tab, label: 'Crear varios', icon: Users },
    { id: 'import' as Tab, label: 'Importar CSV', icon: Upload },
  ]

  // Shared form component
  const renderForm = (buttonLabel: string, onSubmit: () => void) => (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="student-email" className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
          <input
            type="email"
            id="student-email"
            value={form.email}
            onChange={e => updateForm('email', e.target.value)}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4]"
            placeholder="alumno@email.com"
          />
        </div>
        <div>
          <label htmlFor="student-password"  className="block text-sm font-medium text-gray-700 mb-1">Contraseña *</label>
          <input
            type="text"
            id="student-password"
            value={form.password}
            onChange={e => updateForm('password', e.target.value)}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4]"
            placeholder="Mínimo 6 caracteres"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="student-first-name" className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
          <input
            type="text"
            id="student-first-name"
            value={form.first_name}
            onChange={e => updateForm('first_name', e.target.value)}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4]"
            placeholder="Nombre"
          />
        </div>
        <div>
          <label htmlFor="student-last-name" className="block text-sm font-medium text-gray-700 mb-1">Apellido *</label>
          <input
            type="text"
            id="student-last-name"
            value={form.last_name}
            onChange={e => updateForm('last_name', e.target.value)}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4]"
            placeholder="Apellido"
          />
        </div>
      </div>

      <div>
        <label htmlFor="student-phone"  className="block text-sm font-medium text-gray-700 mb-1">Teléfono</label>
        <input
          type="tel"
          id="student-phone"
          value={form.phone}
          onChange={e => updateForm('phone', e.target.value)}
          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4]"
          placeholder="+56 9 1234 5678"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="student-start-date"  className="block text-sm font-medium text-gray-700 mb-1">Fecha de inicio del programa</label>
          <input
            type="date"
            id="student-start-date"
            value={form.program_start_date}
            onChange={e => updateForm('program_start_date', e.target.value)}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4]"
          />
        </div>
        <div>
          <label htmlFor="student-birth-date"  className="block text-sm font-medium text-gray-700 mb-1">Fecha de nacimiento *</label>
          <input
            type="date"
            id="student-birth-date"
            value={form.birth_date}
            onChange={e => updateForm('birth_date', e.target.value)}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4]"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label htmlFor="student-age-group" className="block text-sm font-medium text-gray-700 mb-1">Grupo de edad</label>
          <select
            id="student-age-group"
            value={form.age_group}
            onChange={e => updateForm('age_group', e.target.value)}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4]"
          >
            <option value="adult">Adulto (18+)</option>
            <option value="children">Niño/Joven</option>
          </select>
        </div>
        <div>
          <label htmlFor="student-subscription" className="block text-sm font-medium text-gray-700 mb-1">Suscripción</label>
          <select
            id="student-subscription"
            value={form.subscription_status}
            onChange={e => updateForm('subscription_status', e.target.value)}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4]"
          >
            <option value="trial">Trial (gratuito)</option>
            <option value="active">Premium (completo)</option>
          </select>
        </div>
        <div>
          <label htmlFor="student-payment" className="block text-sm font-medium text-gray-700 mb-1">Monto del programa (CLP)</label>
          <input
            type="number"
            id="student-payment"
            value={form.payment_amount}
            onChange={e => updateForm('payment_amount', e.target.value)}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4]"
            placeholder="Ej: 45000"
            min="0"
          />
        </div>
      </div>

      {/* Single result message */}
      {singleResult && (
        <div className={`flex items-center gap-2 p-3 rounded-lg ${
          singleResult.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
        }`}>
          {singleResult.success ? <CheckCircle className="w-5 h-5 shrink-0" /> : <XCircle className="w-5 h-5 shrink-0" />}
          <span className="text-sm">
            {singleResult.success
              ? `Alumno ${singleResult.name} creado exitosamente`
              : singleResult.error}
          </span>
        </div>
      )}

      <button
        onClick={onSubmit}
        disabled={isCreating}
        className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#0055A4] text-white rounded-lg font-medium hover:bg-[#004080] disabled:opacity-50 transition-colors"
      >
        {isCreating && <Loader2 className="w-4 h-4 animate-spin" />}
        {buttonLabel}
      </button>
    </div>
  )

  const renderBatchResults = () => (
    <BatchResults results={batchResults} progress={batchProgress} isRunning={isBatchRunning} />
  )

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <Link
          to="/admin/users"
          className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-700 mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Volver a usuarios
        </Link>
        <h1 className="text-2xl font-bold text-gray-900">Crear alumnos</h1>
        <p className="text-gray-600">Crea alumnos individualmente, en grupo o importa desde CSV</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => {
              setActiveTab(tab.id)
              setSingleResult(null)
              setBatchResults([])
            }}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? 'bg-white text-[#0055A4] shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-xl shadow-sm border p-6">

        {/* TAB: Single */}
        {activeTab === 'single' && (
          <div>
            <h2 className="text-lg font-semibold mb-4">Crear un alumno</h2>
            {renderForm('Crear alumno', handleCreateSingle)}
          </div>
        )}

        {/* TAB: Batch */}
        {activeTab === 'batch' && (
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-semibold mb-4">Agregar alumnos a la lista</h2>
              {renderForm('+ Agregar a la lista', handleAddToBatch)}
            </div>

            {/* Batch list */}
            {batchList.length > 0 && (
              <div className="border-t pt-6">
                <h3 className="font-semibold mb-3">
                  Lista de alumnos ({batchList.length})
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Nombre</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Nacimiento</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Grupo</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Plan</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Monto</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {batchList.map((s, i) => (
                        <tr key={i} className="hover:bg-gray-50">
                          <td className="px-4 py-2 text-gray-500">{i + 1}</td>
                          <td className="px-4 py-2">{s.email}</td>
                          <td className="px-4 py-2">{s.first_name} {s.last_name}</td>
                          <td className="px-4 py-2">{s.birth_date}</td>
                          <td className="px-4 py-2">{s.age_group === 'adult' ? 'Adulto' : 'Niño'}</td>
                          <td className="px-4 py-2">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              s.subscription_status === 'active' ? 'bg-yellow-100 text-yellow-700' : 'bg-[#0055A4]/10 text-[#0055A4]'
                            }`}>
                              {s.subscription_status === 'active' ? 'Premium' : 'Trial'}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-right text-sm">
                            {s.payment_amount ? `$${Number(s.payment_amount).toLocaleString('es-CL')}` : '-'}
                          </td>
                          <td className="px-4 py-2 text-right">
                            <button
                              onClick={() => handleRemoveFromBatch(i)}
                              disabled={isBatchRunning}
                              className="p-1 text-gray-400 hover:text-red-500 disabled:opacity-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="mt-4 flex items-center gap-4">
                  <button
                    onClick={handleCreateBatch}
                    disabled={isBatchRunning || batchList.length === 0}
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:opacity-50 transition-colors"
                  >
                    {isBatchRunning && <Loader2 className="w-4 h-4 animate-spin" />}
                    Crear {batchList.length} alumno{batchList.length !== 1 ? 's' : ''}
                  </button>
                  {!isBatchRunning && batchList.length > 0 && (
                    <button
                      onClick={() => { setBatchList([]); setBatchResults([]) }}
                      className="text-sm text-gray-500 hover:text-red-500"
                    >
                      Limpiar lista
                    </button>
                  )}
                </div>

                {renderBatchResults()}
              </div>
            )}
          </div>
        )}

        {/* TAB: Import */}
        {activeTab === 'import' && (
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-semibold mb-2">Importar desde CSV</h2>
              <p className="text-sm text-gray-600 mb-4">
                Sube un archivo CSV con los datos de los alumnos. La primera fila debe ser el encabezado.
              </p>
            </div>

            {/* Format info */}
            <div className="bg-[#0055A4]/5 border border-[#0055A4]/20 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-[#0055A4] shrink-0 mt-0.5" />
                <div className="text-sm text-[#003d7a]">
                  <p className="font-medium mb-1">Formato del CSV:</p>
                  <code className="text-xs bg-[#0055A4]/10 px-2 py-1 rounded block mb-2">
                    email,password,nombre,apellido,telefono,fecha_nacimiento,grupo_edad,suscripcion
                  </code>
                  <p className="text-xs">
                    <strong>grupo_edad</strong>: adult | children &nbsp;&nbsp;{' '}
                    <strong>suscripcion</strong>: trial | active &nbsp;&nbsp;{' '}
                    <strong>fecha</strong>: YYYY-MM-DD
                  </p>
                </div>
              </div>
            </div>

            {/* Monto total para dividir entre alumnos */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <label htmlFor="csv-total-amount" className="block text-sm font-medium text-amber-800 mb-2">
                Monto total del grupo (CLP)
              </label>
              <input id="csv-total-amount" type="number" value={csvTotalAmount}
                onChange={e => setCsvTotalAmount(e.target.value)}
                className="w-full md:w-64 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                placeholder="Ej: 500000"
                min="0"
              />
              <p className="text-xs text-amber-600 mt-1">
                Este monto se dividirá entre el total de alumnos importados.
                {csvData.length > 0 && csvTotalAmount && Number.parseFloat(csvTotalAmount) > 0 && (
                  <span className="font-semibold">
                    {' '}= ${Math.round(Number.parseFloat(csvTotalAmount) / csvData.length).toLocaleString('es-CL')} por alumno ({csvData.length} alumnos)
                  </span>
                )}
              </p>
            </div>

            <div className="flex items-center gap-4">
              {/* File upload */}
              <label className="flex-1 border-2 border-dashed border-gray-300 rounded-xl p-8 text-center cursor-pointer hover:border-[#0055A4]/40 hover:bg-[#0055A4]/5 transition-colors">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.txt"
                  onChange={handleFileUpload}
                  className="hidden"
                />
                <FileText className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                <p className="font-medium text-gray-700">Arrastra un archivo CSV o haz clic para seleccionar</p>
                <p className="text-sm text-gray-500 mt-1">Formato: .csv</p>
              </label>
            </div>

            <button
              onClick={downloadTemplate}
              className="inline-flex items-center gap-2 text-sm text-[#0055A4] hover:text-[#003d7a]"
            >
              <Download className="w-4 h-4" />
              Descargar plantilla CSV
            </button>

            {csvError && (
              <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg">
                <XCircle className="w-5 h-5 shrink-0" />
                <span className="text-sm">{csvError}</span>
              </div>
            )}

            {/* CSV Preview */}
            {csvData.length > 0 && (
              <div className="border-t pt-6">
                <h3 className="font-semibold mb-3">
                  Vista previa ({csvData.length} alumnos)
                </h3>
                <div className="overflow-x-auto max-h-80 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b sticky top-0">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Nombre</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Nacimiento</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Grupo</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Plan</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Monto</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {csvData.map((s, i) => {
                        const perAmount = csvTotalAmount && Number.parseFloat(csvTotalAmount) > 0
                          ? Math.round(Number.parseFloat(csvTotalAmount) / csvData.length)
                          : null
                        return (
                        <tr key={i} className="hover:bg-gray-50">
                          <td className="px-4 py-2 text-gray-500">{i + 1}</td>
                          <td className="px-4 py-2">{s.email}</td>
                          <td className="px-4 py-2">{s.first_name} {s.last_name}</td>
                          <td className="px-4 py-2">{s.birth_date}</td>
                          <td className="px-4 py-2">{s.age_group === 'adult' ? 'Adulto' : 'Niño'}</td>
                          <td className="px-4 py-2">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              s.subscription_status === 'active' ? 'bg-yellow-100 text-yellow-700' : 'bg-[#0055A4]/10 text-[#0055A4]'
                            }`}>
                              {s.subscription_status === 'active' ? 'Premium' : 'Trial'}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-right text-sm">
                            {perAmount ? `$${perAmount.toLocaleString('es-CL')}` : '-'}
                          </td>
                        </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="mt-4 flex items-center gap-4">
                  <button
                    onClick={handleImportCsv}
                    disabled={isBatchRunning || csvData.length === 0}
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:opacity-50 transition-colors"
                  >
                    {isBatchRunning && <Loader2 className="w-4 h-4 animate-spin" />}
                    Importar {csvData.length} alumno{csvData.length !== 1 ? 's' : ''}
                  </button>
                  {!isBatchRunning && (
                    <button
                      onClick={() => {
                        setCsvData([])
                        setBatchResults([])
                        if (fileInputRef.current) fileInputRef.current.value = ''
                      }}
                      className="text-sm text-gray-500 hover:text-red-500"
                    >
                      Limpiar
                    </button>
                  )}
                </div>

                {renderBatchResults()}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
